import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  ArrowBack,
  Assignment,
  Autorenew,
  Biotech,
  Medication,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { CircularLoading, NoDataWarning } from "@/components/common";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { BundlePatientSummary } from "@/services/fhir";
import { HTTPResponseStatus } from "@/utils/enum";
import { useFhirData } from "@/utils/useFhirData";
import { IframeDialog } from "@/pages/ehrrecords/components/IframeDialog";
import { MedicalNotes } from "@/pages/ehrrecords/components/patientSummaryAccordions/Documents/components/MedicalNotes";
import { NoteReport } from "@/pages/ehrrecords/components/patientSummaryAccordions/Documents/components/NoteReport";
import { Solicitations } from "@/pages/ehrrecords/components/patientSummaryAccordions/Documents/components/Solicitations";
import { useProcessedDocumentLists } from "@/pages/ehrrecords/components/patientSummaryAccordions/Documents/useProcessedDocumentLists";
import {
  canReissueCertificate,
  canReissuePrescription,
  canReissueSolicitation,
} from "@/pages/patientsearch/components/canReissueDocument";
import type { ReissueDocumentType } from "@/pages/patientsearch/components/DocumentReissueShortcutDialog";

type DocumentSubStep = "select-type" | "list";

interface ReissueSelectDocumentStepProps {
  patientId: string;
  patientName?: string;
  onBackToPatient: () => void;
}

const TYPE_CARDS: Array<{
  type: ReissueDocumentType;
  title: string;
  description: string;
  icon: ReactNode;
}> = [
  {
    type: "prescription",
    title: "Receitas médicas",
    description: "Prescrições assinadas disponíveis para reemissão",
    icon: <Medication color="primary" sx={{ fontSize: 36 }} />,
  },
  {
    type: "solicitation",
    title: "Solicitações de exame",
    description: "Pedidos de exame assinados disponíveis para reemissão",
    icon: <Biotech color="primary" sx={{ fontSize: 36 }} />,
  },
  {
    type: "certificate",
    title: "Relatórios e atestados",
    description: "Relatórios e atestados assinados disponíveis para reemissão",
    icon: <Assignment color="primary" sx={{ fontSize: 36 }} />,
  },
];

/**
 * Passos 2–3 do wizard da agenda: tipo de documento → lista → reemitir
 * (mesma regra do DocumentReissueShortcutDialog, em layout inline).
 */
export function ReissueSelectDocumentStep({
  patientId,
  patientName,
  onBackToPatient,
}: ReissueSelectDocumentStepProps) {
  const { access } = useSessionContext();
  const { userPractitionerRole } = usePractitionerRoleContext();

  const [subStep, setSubStep] = useState<DocumentSubStep>("select-type");
  const [selectedType, setSelectedType] = useState<ReissueDocumentType | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<any>();
  const [refetch, setRefetch] = useState(false);

  const reissueCtx = useMemo(
    () => ({
      accessType: access?.type,
      practitionerRoleId: userPractitionerRole?.id,
    }),
    [access?.type, userPractitionerRole?.id],
  );

  const [viewerOpen, setViewerOpen] = useState(false);
  const [binaryUrl, setBinaryUrl] = useState("");

  const handleOpenViewer = () => setViewerOpen(true);
  const handleCloseViewer = () => {
    setBinaryUrl("");
    setViewerOpen(false);
  };

  const loadSummary = useCallback(async () => {
    if (!patientId) return;

    setLoading(true);
    try {
      const bundleResponse = await BundlePatientSummary({
        identifier: patientId,
        purposeofuse: "Atendimento",
      });

      if (bundleResponse?.status === HTTPResponseStatus.Forbidden) {
        toast.warn("Verifique as opções de consentimento");
        setSummaryData(undefined);
        return;
      }

      setSummaryData(bundleResponse);
    } catch (err) {
      console.error("[ReissueSelectDocumentStep] Bundle error:", err);
      toast.error("Não foi possível carregar os documentos do paciente.");
      setSummaryData(undefined);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary, refetch]);

  const medicalNoteData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "prescricao",
  )?.[0]?.entry;

  const solicitationData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "service-request",
  )?.[0]?.entry;

  const NoteReportData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "atestado",
  )?.[0]?.entry;

  const resultsData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "resultado",
  );

  const {
    processedMedicalNoteData: allMedicalNotes,
    processedSolicitationData: allSolicitations,
    processedNoteReportData: allNoteReports,
  } = useProcessedDocumentLists({
    medicalNoteData,
    solicitationData,
    resultsData,
    NoteReportData,
    sign: true,
  });

  const processedMedicalNoteData = useMemo(
    () =>
      allMedicalNotes.filter((row: any) =>
        canReissuePrescription(row, reissueCtx),
      ),
    [allMedicalNotes, reissueCtx],
  );

  const processedSolicitationData = useMemo(
    () =>
      allSolicitations.filter((row: any) =>
        canReissueSolicitation(row, reissueCtx),
      ),
    [allSolicitations, reissueCtx],
  );

  const processedNoteReportData = useMemo(
    () =>
      allNoteReports.filter((row: any) =>
        canReissueCertificate(row, reissueCtx),
      ),
    [allNoteReports, reissueCtx],
  );

  const countByType: Record<ReissueDocumentType, number> = {
    prescription: processedMedicalNoteData.length,
    solicitation: processedSolicitationData.length,
    certificate: processedNoteReportData.length,
  };

  const titleByType: Record<ReissueDocumentType, string> = {
    prescription: "Receitas médicas",
    solicitation: "Solicitações de exame",
    certificate: "Relatórios e atestados",
  };

  const handleSelectType = (type: ReissueDocumentType) => {
    setSelectedType(type);
    setSubStep("list");
  };

  const handleBackFromList = () => {
    setSelectedType(null);
    setSubStep("select-type");
  };

  const headerTitle =
    subStep === "list" && selectedType
      ? titleByType[selectedType]
      : "Selecione o tipo de documento";

  return (
    <>
      <Stack spacing={2} width="100%">
        <Stack spacing={0.5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {subStep === "list" ? (
              <IconButton
                aria-label="Voltar ao tipo de documento"
                onClick={handleBackFromList}
                size="small"
                edge="start"
              >
                <ArrowBack />
              </IconButton>
            ) : (
              <IconButton
                aria-label="Voltar à seleção de paciente"
                onClick={onBackToPatient}
                size="small"
                edge="start"
              >
                <ArrowBack />
              </IconButton>
            )}
            {subStep === "select-type" ? (
              <Autorenew color="primary" />
            ) : null}
            <Typography variant="subtitle1" fontWeight={600}>
              {headerTitle}
            </Typography>
          </Stack>
          {patientName ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ pl: 5 }}
            >
              Paciente: {patientName}
            </Typography>
          ) : null}
        </Stack>

        {loading && !summaryData ? (
          <Box py={4}>
            <CircularLoading />
          </Box>
        ) : subStep === "select-type" ? (
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
            >
              {TYPE_CARDS.map((card) => {
                const count = countByType[card.type];
                return (
                  <Card
                    key={card.type}
                    variant="outlined"
                    sx={{
                      flex: 1,
                      borderRadius: 2,
                      borderColor: "divider",
                      transition: "border-color 0.15s, box-shadow 0.15s",
                      "&:hover": {
                        borderColor: "primary.main",
                        boxShadow: 1,
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleSelectType(card.type)}
                      sx={{
                        height: "100%",
                        p: 2.5,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 1.5,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                      >
                        {card.icon}
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: 13,
                            fontWeight: 600,
                            bgcolor: "primary100.main",
                            color: "primary1000.main",
                          }}
                        >
                          {count}
                        </Avatar>
                      </Stack>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {card.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {card.description}
                      </Typography>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Stack>

            <Button
              variant="text"
              startIcon={<ArrowBack />}
              onClick={onBackToPatient}
              sx={{ textTransform: "none", alignSelf: "flex-start" }}
            >
              Trocar paciente
            </Button>
          </Stack>
        ) : (
          <Box sx={{ minHeight: 200, position: "relative" }}>
            {selectedType === "prescription" ? (
              processedMedicalNoteData.length === 0 ? (
                <NoDataWarning message="Nenhuma receita disponível para reemissão." />
              ) : (
                <MedicalNotes
                  setRefetch={setRefetch}
                  sign
                  rowData={processedMedicalNoteData}
                  handleOpen={handleOpenViewer}
                  setBinaryUrl={setBinaryUrl}
                />
              )
            ) : selectedType === "solicitation" ? (
              processedSolicitationData.length === 0 ? (
                <NoDataWarning message="Nenhuma solicitação de exame disponível para reemissão." />
              ) : (
                <Solicitations
                  sign
                  setRefetch={setRefetch}
                  handleOpen={handleOpenViewer}
                  setBinaryUrl={setBinaryUrl}
                  rowData={processedSolicitationData}
                  resultsData={resultsData}
                />
              )
            ) : selectedType === "certificate" ? (
              processedNoteReportData.length === 0 ? (
                <NoDataWarning message="Nenhum relatório ou atestado disponível para reemissão." />
              ) : (
                <NoteReport
                  setRefetch={setRefetch}
                  sign
                  rowData={processedNoteReportData}
                  handleOpen={handleOpenViewer}
                  setBinaryUrl={setBinaryUrl}
                />
              )
            ) : null}
          </Box>
        )}
      </Stack>

      {viewerOpen && (
        <IframeDialog
          setRefetchSummary={setRefetch}
          handleClose={handleCloseViewer}
          isOpen={viewerOpen}
          data={binaryUrl}
        />
      )}
    </>
  );
}
